import React, { useEffect, useState } from "react";
import { Wallet, AlertCircle, TrendingUp, Receipt, CheckCircle2, Loader2, FileText } from "lucide-react";
import { api, fmtGNF, fmtDate, openAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AIInsightsCard from "@/components/AIInsightsCard";

export default function FinanceDashboard() {
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [dash, pays] = await Promise.all([
        api.get("/dashboard/finance"),
        api.get("/payments"),
      ]);
      setData(dash.data);
      setPayments(pays.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const validate = async (id) => {
    try {
      await api.patch(`/payments/${id}/validate`);
      toast.success("Paiement validé");
      load();
    } catch { toast.error("Erreur lors de la validation"); }
  };

  const viewReceipt = async (id) => {
    try {
      await openAuthedFile(`/payments/${id}/receipt`);
    } catch { toast.error("Impossible d'ouvrir le reçu PDF"); }
  };

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      <div>
        <div className="chip mb-2"><Wallet className="h-3 w-3" /><span>Service Financier</span></div>
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] tracking-tight">Gestion des paiements</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="kpi-total-paid">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Total encaissé</div>
          <div className="font-heading font-extrabold text-2xl text-[#166534]">{fmtGNF(data?.total_paid)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="kpi-total-pending">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">En attente</div>
          <div className="font-heading font-extrabold text-2xl text-[#EA580C]">{fmtGNF(data?.total_pending)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="kpi-today">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Aujourd&apos;hui</div>
          <div className="font-heading font-extrabold text-2xl text-[#1E3A8A]">{data?.today_count || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="kpi-methods">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Modes utilisés</div>
          <div className="font-heading font-extrabold text-2xl text-[#FBBF24]">{data?.by_method?.length || 0}</div>
        </div>
      </div>

      {/* AI Insights — priorités de relance du jour */}
      <AIInsightsCard accentColor="#166534" />

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="payments-table">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-lg text-[#1E3A8A]">Historique des paiements</h3>
            <p className="text-xs text-slate-500">{payments.length} transactions</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3 text-left">N° Reçu</th>
                <th className="px-5 py-3 text-left">Date</th>
                <th className="px-5 py-3 text-left">Type</th>
                <th className="px-5 py-3 text-left">Mode</th>
                <th className="px-5 py-3 text-right">Montant</th>
                <th className="px-5 py-3 text-center">Statut</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`payment-row-${p.id}`}>
                  <td className="px-5 py-4 text-sm font-mono text-[#1E3A8A]">{p.receipt_number}</td>
                  <td className="px-5 py-4 text-sm text-slate-600">{fmtDate(p.created_at)}</td>
                  <td className="px-5 py-4 text-sm">{({registration: "Inscription", tuition: "Scolarité", books: "Manuels", other: "Autres"})[p.type]}</td>
                  <td className="px-5 py-4 text-sm text-slate-600">{({cash: "Espèces", stripe: "Carte", wire_transfer: "Virement", mobile_money: "Mobile Money"})[p.method]}</td>
                  <td className="px-5 py-4 text-sm font-semibold text-right">{fmtGNF(p.amount)}</td>
                  <td className="px-5 py-4 text-center">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${p.status === "paid" ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"}`}>
                      {p.status === "paid" ? <><CheckCircle2 className="h-3 w-3" /> Payé</> : "En attente"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {p.status === "pending" && (
                        <Button size="sm" onClick={() => validate(p.id)} className="bg-[#166534] hover:bg-[#14532d] text-white text-xs gap-1" data-testid={`validate-${p.id}`}>
                          <CheckCircle2 className="h-3 w-3" /> Valider
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => viewReceipt(p.id)} className="text-xs gap-1" data-testid={`receipt-${p.id}`}>
                        <FileText className="h-3 w-3" /> Reçu PDF
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">Aucun paiement enregistré</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
